/**
 * ProjectOfficialArtifactView — `/projects/$projectId/official/$artifactId`
 *
 * Visor read-only de un artifact del proyecto. Wrapper delgado sobre
 * `ArtifactPreview mode='view'` — el render del documento (header,
 * secciones, metadata) vive en ese componente reutilizable; aquí solo
 * hacemos los fetchs y armamos los slots para este caso (back link
 * a documentos oficiales, panel de metadata).
 *
 * Datos:
 *   1. GET /projects/{id}/snapshot   → templates congeladas con i18n.
 *   2. GET /artifacts/{artifactId}   → metadata + current_version_id.
 *   3. GET /artifacts/{aid}/versions/{vid} → content JSONB.
 *
 * El snapshot manda sobre el catálogo vivo (Doc 06): el viewer SIEMPRE
 * lee la `definition` del snapshot del proyecto, nunca de `templates`
 * actuales — garantiza que el artifact se ve como cuando se aprobó.
 *
 * Idioma: si `project.content_language === 'en'` y el i18n.en del
 * template existe, usa los títulos en inglés. Si no, fallback a `es`.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { GitBranch, MessageSquare, Pencil } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  getArtifact,
  getArtifactVersion,
  submitForReview,
} from '../../api/artifacts'
import { useVersionCitations } from '../../api/citations'
import { listActiveComments, listVersionComments } from '../../api/comments'
import { listProjectMembers } from '../../api/project_members'
import {
  fetchSnapshotTemplate,
  type SnapshotTemplateItem,
} from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { DisabledActionButton } from '../../components/DisabledActionButton'
import { CitationsLayer } from '../../components/editor/CitationsLayer'
import { ApprovalPanel } from '../../components/projects/ApprovalPanel'
import { CommentsViewerPanel } from '../../components/projects/CommentsViewerPanel'
import {
  ArtifactMetadata,
  ArtifactPreview,
} from '../../components/projects/official-viewer/ArtifactPreview'
import { DownloadPdfButton } from '../../components/projects/official-viewer/DownloadPdfButton'
import { NewVersionDialog } from '../../components/projects/official-viewer/NewVersionDialog'
import { UploadedFileViewer } from '../../components/projects/official-viewer/UploadedFileViewer'
import {
  templateI18nSectionMap,
  type TemplateDefinition,
  type TemplateI18nSection,
} from '../../components/projects/official-viewer/section_types'
import { RightSidePanel } from '../../components/RightSidePanel'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { SubmitForReviewDialog } from '../../components/projects/SubmitForReviewDialog'
import { useProjectAccess } from '../../hooks/useProjectAccess'
import {
  canEditArtifact,
  canEditSomeArtifact,
  canManageProjectSources,
  isExternal,
} from '../../lib/tiers'
import { isVersionLockError } from '../../lib/versionLock'
import { pushToast } from '../../stores/toastStore'
import { useBreadcrumbExtension, useProjectContext } from './ProjectShell'
import { useRightPanel } from './rightPanelSlot'

export function ProjectOfficialArtifactView() {
  const { t } = useTranslation(['projects', 'errors'])
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()
  const { me, isLoading: meIsLoading } = useMe()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { artifactId } = useParams({
    from: '/projects/$projectId/official/$artifactId',
  })
  // Doc 21 Fase 4: drawer de comentarios inline.
  const [commentsOpen, setCommentsOpen] = useState(false)
  // Doc 07: dialog "Editar oficial (crear nueva versión)" — solo
  // disponible cuando el documento ya es canon vigente.
  const [newVersionOpen, setNewVersionOpen] = useState(false)
  // OT-0007 — "Enviar a revisión" para un documento en modo carga: el
  // editor de plantilla (`.../edit`) nunca se monta para `source_mode
  // ='upload'`, así que ese submit vive aquí, en el viewer.
  const [submitOpen, setSubmitOpen] = useState(false)

  // 1. Artifact metadata.
  const artifactQuery = useQuery({
    queryKey: ['artifacts', artifactId],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getArtifact(accessToken, artifactId)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  // 2. Solo EL template de este artefacto (no el snapshot completo ~700KB).
  // El snapshot es inmutable → `staleTime: Infinity` (un fetch por sesión).
  const templateKey = artifactQuery.data?.template_key ?? null
  const templateQuery = useQuery({
    queryKey: ['projects', project.id, 'snapshot', 'template', templateKey],
    queryFn: () => {
      if (!accessToken || !templateKey)
        throw new Error('no_access_token_or_template_key')
      return fetchSnapshotTemplate(accessToken, project.id, templateKey)
    },
    enabled: !!accessToken && !!templateKey,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
    retry: false,
  })

  // 3. Versión actual (content) — depende de artifact.current_version_id.
  const currentVersionId = artifactQuery.data?.current_version_id ?? null
  const versionQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', currentVersionId],
    queryFn: () => {
      if (!accessToken || !currentVersionId)
        throw new Error('no_access_token_or_version')
      return getArtifactVersion(accessToken, artifactId, currentVersionId)
    },
    enabled: !!accessToken && !!currentVersionId,
    staleTime: 60_000,
    retry: false,
  })

  // OT-0009 B1 — "Continuar editando borrador" y "Editar oficial (nueva
  // versión)" no verificaban permiso (D11: dueño ∪ líder ∪ admin) antes
  // de ofrecerse. Mismo queryKey que otras vistas del proyecto — si el
  // usuario ya pasó por Lifecycle/Equipo/Fuentes, TanStack Query sirve
  // el cache sin fetch nuevo.
  //
  // Deshabilitada para `external`: `can_view_project` (y por tanto listar
  // members) siempre rechaza a ese tier — la query fallaría y bloqueaba
  // la página entera (incluido el ApprovalPanel que un aprobador externo
  // sí necesita), aun cuando `isMember`/`isLead` no cambian el resultado
  // de `canEditArtifact` para external (ya lo excluye `canOwnArtifact`
  // antes de mirar esos dos campos).
  //
  // `!meIsLoading` evita la carrera: sin esto, `me?.role` todavía es
  // `undefined` en el primer render, `isExternal(undefined)` da `false`,
  // y la query se dispara antes de saber que el tier es external — el
  // 404 queda pegado en cache aunque un instante después ya se sepa que
  // no debía pedirse.
  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken && !meIsLoading && !isExternal(me?.role),
    staleTime: 30_000,
    retry: false,
  })
  const { isMember, isLead, section } = useProjectAccess(project, membersQuery.data ?? [])

  // 3.b. Draft post-canon activo (Doc 07): mientras existe y no se ha
  // sometido a revisión, `current_version_id` sigue apuntando al canon
  // vigente — la v(N+1) solo vive en `active_draft_version_id`. Para
  // `source_mode='upload'` este visor ES el editor (no hay `.../edit`),
  // así que necesita el content/blob_refs de esa v(N+1), no del canon.
  const activeDraftVersionId = artifactQuery.data?.active_draft_version_id ?? null
  const draftVersionQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', activeDraftVersionId],
    queryFn: () => {
      if (!accessToken || !activeDraftVersionId)
        throw new Error('no_access_token_or_version')
      return getArtifactVersion(accessToken, artifactId, activeDraftVersionId)
    },
    enabled: !!accessToken && !!activeDraftVersionId,
    staleTime: 60_000,
    retry: false,
  })

  // Versión "de trabajo" para las acciones de modo carga: la draft
  // post-canon si hay una abierta, si no la current (que ya cubre los
  // casos v1 inicial y v(N+1) rechazada — en ambos `active_draft_version_id`
  // es null y `current_version_id` ES la versión editable).
  const workingVersionId = activeDraftVersionId ?? currentVersionId
  // OT-0064 — "Continuar editando borrador" sólo se ofrece si el servidor
  // dice que esa versión es editable (`is_editable`); el permiso del
  // usuario (`canEditArtifact`) se evalúa aparte, abajo.
  const activeDraftEditable = draftVersionQuery.data?.is_editable === true

  // ADR-0001 Tier 1 — citas IA persistidas por versión. CitationsLayer
  // las consume para mostrar el popover al click en `[N]` del documento
  // oficial. Antes solo el editor montaba esta capa; ahora también el
  // viewer (read-only) y el preview, para que el lector vea las fuentes.
  const citationsQuery = useVersionCitations({
    artifactId,
    versionId: currentVersionId,
  })

  // Doc 21 Fase 5.f — mismo gate v−1 del editor: no se puede someter a
  // revisión con comments del cycle anterior sin resolver. Necesario
  // aquí porque el submit de un documento en modo carga vive en este
  // viewer, no en `.../edit`.
  const activeCommentsQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'active-comments', workingVersionId],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listActiveComments(accessToken, artifactId, {
        versionId: workingVersionId,
      })
    },
    enabled: !!accessToken,
    staleTime: 15_000,
    select: (rows) => rows.filter((r) => r.kind === 'comment').length,
  })
  const activeCommentsCount = activeCommentsQuery.data ?? 0

  // Espejo de `can_manage_project_sources` (backend) para gatear la zona
  // de carga/reemplazo/borrado en `UploadedFileViewer`: tier de edición
  // NO basta, hace falta además ser miembro de ESTE proyecto (o admin
  // del tenant). Antes ese componente solo miraba el tier — un editor
  // sin membresía en el proyecto veía los controles y el backend se los
  // rechazaba con 403 al primer click.
  // La membresía sale de `useProjectAccess` arriba, que ya consume la
  // única `membersQuery` de esta vista. Antes había una segunda query
  // con el mismo `queryKey` y su propio cálculo de `isProjectMember`:
  // dos fuentes de verdad para el mismo dato.
  const canManageFile = canManageProjectSources(me?.role, me?.tenant?.access, { isMember, section })

  // ─── Derive template definition desde snapshot ─────────────────────────
  const snapshotTemplate: SnapshotTemplateItem | null =
    templateQuery.data ?? null

  const templateDef: TemplateDefinition | null = useMemo(() => {
    if (!snapshotTemplate) return null
    return snapshotTemplate.definition as unknown as TemplateDefinition
  }, [snapshotTemplate])

  // Breadcrumb extension: "› Lifecycle › <artifact name>"
  const artifactNameForBreadcrumb =
    artifactQuery.data?.name ?? artifactQuery.data?.template_key ?? '…'
  useBreadcrumbExtension([
    { label: t('shell.tabs.lifecycle') },
    { label: artifactNameForBreadcrumb },
  ])

  // i18n locale resolution: prioriza content_language del proyecto si el
  // template lo tiene; fallback a 'es'.
  const renderLocale = useMemo<'es' | 'en'>(() => {
    if (!templateDef?.i18n) return 'es'
    const projectLang = project.content_language
    if (
      (projectLang === 'es' || projectLang === 'en') &&
      templateDef.i18n[projectLang]
    ) {
      return projectLang
    }
    if (templateDef.i18n.es) return 'es'
    if (templateDef.i18n.en) return 'en'
    return 'es'
  }, [templateDef, project.content_language])

  const templateI18nMap = useMemo(
    () =>
      templateDef
        ? templateI18nSectionMap(templateDef, renderLocale)
        : new Map<string, TemplateI18nSection>(),
    [templateDef, renderLocale],
  )

  // ─── Render gates ────────────────────────────────────────────────────────
  if (
    meIsLoading ||
    artifactQuery.isLoading ||
    templateQuery.isLoading ||
    membersQuery.isLoading ||
    (currentVersionId && versionQuery.isLoading) ||
    (activeDraftVersionId && draftVersionQuery.isLoading)
  ) {
    return (
      <ScrollPage padding="tight">
        <p className="font-mono text-xs text-ink-subtle">
          {t('official_viewer.loading')}
        </p>
      </ScrollPage>
    )
  }

  if (templateQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={templateQuery.error} />
      </ScrollPage>
    )
  }
  if (artifactQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={artifactQuery.error} />
      </ScrollPage>
    )
  }
  // `membersQuery` NO corta el render. Solo alimenta `isMember`/`isLead`,
  // que deciden si una acción se ofrece activa o deshabilitada — es una
  // pista de permisos, no el contenido de la página. Tumbar la lectura
  // del documento oficial porque falló listar miembros invierte la
  // mitigación de la propia OT-0009 (§8: "ante la duda, habilitar") y
  // convierte un fallo transitorio en una página inservible. Si falla,
  // `membersQuery.data ?? []` deja `isMember`/`isLead` en `false`: se
  // ofrece de menos, que es el lado seguro, y el backend sigue siendo
  // quien enforce.

  const artifact = artifactQuery.data
  if (!artifact) return null

  const backLink = {
    label: t('official_viewer.back_to_official'),
    fallback: () =>
      void navigate({
        to: '/projects/$projectId/official',
        params: { projectId: project.id },
      }),
  }

  // El artifact existe pero no tiene versión actual — mensaje honesto.
  if (!currentVersionId) {
    return (
      <ScrollPage padding="tight">
        <div className="max-w-5xl">
          <div className="rounded-md border border-review/40 bg-review-soft/40 p-4 font-body text-sm text-ink-muted">
            {t('official_viewer.errors.no_current_version')}
          </div>
        </div>
      </ScrollPage>
    )
  }

  if (versionQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={versionQuery.error} />
      </ScrollPage>
    )
  }
  if (draftVersionQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={draftVersionQuery.error} />
      </ScrollPage>
    )
  }
  const version = versionQuery.data
  if (!version) return null

  // OT-0007 — versión de trabajo para el body/submit de `source_mode
  // ='upload'`: la draft post-canon cuando hay una abierta (§3.b más
  // arriba), si no la misma `version` de la canon/draft-legacy.
  const uploadWorkingVersion = activeDraftVersionId
    ? draftVersionQuery.data ?? null
    : version

  // Template del snapshot ausente — situación rara pero defensiva.
  if (!snapshotTemplate || !templateDef) {
    return (
      <ScrollPage padding="tight">
        <div className="max-w-5xl">
          <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-body text-sm text-ink-muted">
            {t('official_viewer.errors.template_not_in_snapshot', {
              template_key: artifact.template_key,
            })}
          </div>
        </div>
      </ScrollPage>
    )
  }

  // Doc 21 — Fase 2.c: ApprovalPanel se monta como banner cuando el
  // artifact está en revisión. Para artifacts approved/superseded el
  // panel también se muestra (read-only) — sirve como historial de
  // quién aprobó qué versión.
  const showApprovalPanel =
    artifact.status === 'in_review' ||
    artifact.status === 'approved' ||
    artifact.status === 'superseded'

  // OT-0009 B1 — dueño ∪ líder ∪ admin (D11), mismo predicado que
  // `PhaseDrillIn` y `ProjectOfficialArtifactEditView`. `editingTierAtAll`
  // separa "tu tier no edita nunca" (oculto) de "tu tier edita, pero no
  // este documento" (deshabilitado + tooltip).
  const editingTierAtAll = canEditSomeArtifact(me?.role, me?.tenant?.access)
  const canEditThisArtifact = canEditArtifact(me?.role, me?.tenant?.access, {
    isMember,
    isLead,
    section,
    isOwner: artifact.owner_user_id === me?.id,
  })

  // Doc 07 — Lógica de acción de edición:
  //  · IN_REVIEW: NUNCA mostrar botón de edición (immutability: el doc
  //    bajo revisión está congelado; el reviewer firma exactamente lo
  //    que ve). Para editar el draft v(N+1) bajo revisión, el reviewer
  //    debe primero rechazar — el artifact vuelve a DRAFT y entonces el
  //    botón "Editar" aparece naturalmente.
  //  · Hay draft post-canon (status=approved + active_draft):
  //    "Continuar editando borrador".
  //  · Es canon vigente sin draft activa: "Editar oficial (nueva
  //    versión)" — abre el dialog para crear v(N+1).
  const editAction =
    // OT-0009 B1 — `!editingTierAtAll` corta arriba: un tier que no
    // produce contenido no ve NINGUNA de estas acciones. Debajo, cada
    // rama usa `canEditThisArtifact` (D11: dueño ∪ líder ∪ admin) para
    // decidir entre acción activa y deshabilitada-con-motivo, en vez de
    // ofrecerla y que el backend la rechace con 403 tras escribir.
    artifact.status === 'in_review' || !editingTierAtAll
      ? null
      : artifact.source_mode === 'upload' &&
          (artifact.active_draft_version_id || artifact.status === 'draft') ? (
        // OT-0007 — un documento en modo carga nunca pasa por `.../edit`
        // (no hay secciones): el submit vive acá, tanto para la v1/draft
        // rechazada como para una v(N+1) post-canon sin someter.
        // `uploadWorkingVersion` resuelve cuál de las dos aplica.
        canEditThisArtifact ? (
          <button
            type="button"
            onClick={() => setSubmitOpen(true)}
            disabled={
              !uploadWorkingVersion ||
              uploadWorkingVersion.blob_refs.length === 0 ||
              activeCommentsCount > 0
            }
            title={
              !uploadWorkingVersion ||
              uploadWorkingVersion.blob_refs.length === 0
                ? t('errors:ARTIFACT_FILE_REQUIRED')
                : activeCommentsCount > 0
                  ? t('official_editor.submit_blocked_by_comments', {
                      count: activeCommentsCount,
                    })
                  : t('official_viewer.uploaded_file.submit_ready_hint')
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-canvas hover:bg-brand-deep transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GitBranch className="w-3.5 h-3.5" aria-hidden="true" />
            {t('official_editor.submit_for_review_button')}
            {activeCommentsCount > 0 ? (
              <span className="px-1.5 py-0.5 rounded-full bg-canvas/20 text-[10px] font-mono">
                {activeCommentsCount}
              </span>
            ) : null}
          </button>
        ) : (
          <DisabledActionButton
            title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink-subtle cursor-not-allowed"
          >
            <GitBranch className="w-3.5 h-3.5" aria-hidden="true" />
            {t('official_editor.submit_for_review_button')}
          </DisabledActionButton>
        )
      ) : artifact.active_draft_version_id ? (
        !activeDraftEditable ? null : canEditThisArtifact ? (
          <Link
            to="/projects/$projectId/official/$artifactId/edit"
            params={{ projectId: project.id, artifactId: artifact.id }}
            search={{ v: artifact.active_draft_version_id }}
            className="inline-flex items-center gap-1.5 rounded-md border border-review bg-review-soft px-3 py-1.5 text-xs font-medium text-review hover:bg-review-soft/80 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
            {t('official_viewer.active_draft_banner.edit_button')}
          </Link>
        ) : (
          <DisabledActionButton
            title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink-subtle cursor-not-allowed"
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
            {t('official_viewer.active_draft_banner.edit_button')}
          </DisabledActionButton>
        )
      ) : artifact.is_canon && version.version_number ? (
        canEditThisArtifact ? (
          <button
            type="button"
            onClick={() => setNewVersionOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs text-ink hover:border-line-strong hover:bg-surface-muted transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
            {t('official_viewer.new_version.open_button')}
          </button>
        ) : (
          <DisabledActionButton
            title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs text-ink-subtle cursor-not-allowed"
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
            {t('official_viewer.new_version.open_button')}
          </DisabledActionButton>
        )
      ) : null

  return (
    // Layout flex column: toolbar arriba (shrink-0) + contenido
    // scrolleable abajo (flex-1). Mismo patrón que el editor — la
    // toolbar nunca se pierde con el scroll del documento.
    <div className="h-full flex flex-col min-h-0">
      {/* Wrapper `max-w-5xl` alinea los botones al ras del borde derecho
          del documento (misma columna de contenido), no al borde de la
          pantalla. */}
      <div className="shrink-0 bg-surface border-b border-line px-8 py-2.5">
        <div className="max-w-5xl flex items-center justify-end gap-2">
          {/* Orden: acciones primero, "Comentarios" SIEMPRE último a la
              derecha (anchor estable para futuros botones de
              export/descarga). */}
          {editAction}
          <CommentsTrigger
            artifactId={artifact.id}
            versionId={version.id}
            isOpen={commentsOpen}
            onToggle={() => setCommentsOpen((v) => !v)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-8 py-8">
        <CitationsLayer citations={citationsQuery.data ?? []}>
        <ArtifactPreview
          artifact={artifact}
          version={version}
          templateDef={templateDef}
          renderLocale={renderLocale}
          mode="view"
          backLink={backLink}
          topToolbar={<DownloadPdfButton artifact={artifact} version={version} />}
          bodySlot={
            artifact.source_mode === 'upload' && uploadWorkingVersion ? (
              <UploadedFileViewer
                artifact={artifact}
                version={uploadWorkingVersion}
                canManageFile={canManageFile}
              />
            ) : undefined
          }
          banner={
            <>
              {/* Doc 07 — banner cuando hay draft post-canon activa.
                  El user llega al viewer y ve inmediatamente que hay
                  trabajo en progreso, con link directo al editor. */}
              {artifact.active_draft_version_id &&
              artifact.active_draft_version_number &&
              version.version_number ? (
                <div className="mb-3 rounded-md border border-review/30 bg-review-soft/40 px-4 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm font-semibold text-ink">
                      {t('official_viewer.active_draft_banner.title', {
                        n: artifact.active_draft_version_number,
                      })}
                    </p>
                    <p className="mt-0.5 font-body text-xs text-ink-muted">
                      {artifact.active_draft_change_reason
                        ? t(
                            'official_viewer.active_draft_banner.body_with_reason',
                            {
                              reason: artifact.active_draft_change_reason,
                              canon: version.version_number,
                            },
                          )
                        : t(
                            'official_viewer.active_draft_banner.body_no_reason',
                            { canon: version.version_number },
                          )}
                    </p>
                  </div>
                  {/* OT-0007 — en modo carga no hay `.../edit` que aplique
                      (no hay secciones): someter esta v(N+1) vive en
                      `editAction`, arriba en la toolbar.
                      OT-0009 B1 — y cuando sí aplica, el control usa el mismo
                      predicado que la toolbar. Ocultar la acción no oculta el
                      texto de arriba: quien no puede editar igual debe saber
                      que hay una draft en progreso (M2). */}
                  {artifact.source_mode === 'upload' ||
                  !editingTierAtAll ||
                  !activeDraftEditable ? null : canEditThisArtifact ? (
                    <Link
                      to="/projects/$projectId/official/$artifactId/edit"
                      params={{
                        projectId: project.id,
                        artifactId: artifact.id,
                      }}
                      search={{ v: artifact.active_draft_version_id }}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-review px-3 py-1.5 text-xs font-medium text-canvas hover:opacity-90 transition-opacity"
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                      {t('official_viewer.active_draft_banner.edit_button')}
                    </Link>
                  ) : (
                    <DisabledActionButton
                      title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink-subtle cursor-not-allowed"
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                      {t('official_viewer.active_draft_banner.edit_button')}
                    </DisabledActionButton>
                  )}
                </div>
              ) : null}
              {showApprovalPanel ? (
                <ApprovalPanel
                  artifactId={artifact.id}
                  versionId={version.id}
                  reviewCycle={version.review_cycle}
                  isCanon={artifact.is_canon}
                  currentUserId={me?.id ?? null}
                  locale={renderLocale}
                  templateI18nMap={templateI18nMap}
                />
              ) : null}
            </>
          }
          metadataExtras={
            <ArtifactMetadata
              artifact={artifact}
              versionId={version.id}
              versionNumber={version.version_number}
              versionHash={version.hash}
              versionApprovedAt={version.approved_at}
              versionIndexedAt={version.indexed_at}
              contentLanguage={version.content_language}
              subphaseCode={
                (templateDef as Record<string, unknown>)['subphase_id'] as
                  | string
                  | undefined
              }
              templateKey={artifact.template_key}
              templateVersion={artifact.template_version}
            />
          }
        />
        </CitationsLayer>
      </div>

      {/* Dialog new-version — montado siempre, controlado por `open`. */}
      {artifact.is_canon && version.version_number ? (
        <NewVersionDialog
          open={newVersionOpen}
          onClose={() => setNewVersionOpen(false)}
          artifactId={artifact.id}
          projectId={project.id}
          currentVersionNumber={version.version_number}
          sourceMode={artifact.source_mode}
        />
      ) : null}
      {/* OT-0007 — submit-for-review de un documento en modo carga. */}
      <SubmitForReviewDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        artifactId={artifact.id}
        projectId={project.id}
        subphaseCode={artifact.subphase_code}
        currentUserId={me?.id ?? null}
        authorUserId={artifact.created_by_user_id}
        onSubmit={async () => {
          if (!accessToken) throw new Error('no_access_token')
          try {
            await submitForReview(accessToken, artifact.id, {}, workingVersionId)
          } catch (err) {
            // Rechazo por estado (ya en revisión o dejó de ser borrador):
            // el diálogo muestra el motivo y el visor se refresca para dejar
            // de ofrecer el envío.
            if (isVersionLockError(err)) {
              await queryClient.invalidateQueries({ queryKey: ['artifacts', artifact.id] })
            }
            throw err
          }
          await queryClient.invalidateQueries({
            queryKey: ['artifacts', artifact.id],
          })
          await queryClient.invalidateQueries({
            queryKey: ['projects', project.id, 'artifacts'],
          })
          await queryClient.invalidateQueries({
            queryKey: ['projects', project.id, 'lifecycle'],
          })
          pushToast({ kind: 'success', message: t('submit_dialog.toast_success') })
        }}
      />
      {/* Mount-only helper que llama `useRightPanel` cuando ya tenemos
          datos. Vive dentro del componente padre (mismo árbol React)
          y se monta solo cuando hay artifact + version + template
          válidos, lo que respeta las rules-of-hooks (siempre se llama
          en el mismo orden mientras esté montado).  */}
      <CommentsPanelSlot
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        artifact={artifact}
        version={version}
        templateDef={templateDef}
        renderLocale={renderLocale}
        projectId={project.id}
        currentUserId={me?.id ?? null}
        isLead={isLead}
      />
    </div>
  )
}

// ─── Comments panel slot ─────────────────────────────────────────────
// Componente "headless" que solo registra el panel de comentarios en
// el slot derecho del ProjectShell vía `useRightPanel`. No renderiza
// nada visible en este árbol (el panel aparece en la columna derecha
// del shell). Aislarlo en un sub-componente garantiza que el hook
// `useRightPanel` se llame siempre en el mismo punto del árbol cuando
// el padre ya tiene datos válidos.
interface CommentsPanelSlotProps {
  open: boolean
  onClose: () => void
  artifact: {
    id: string
    created_by_user_id: string | null
  }
  /** Doc 21 Fase 10 — incluimos `content` para que el panel pueda
   *  calcular el diff snapshot vs vivo en cada `CommentDiffBlock`. */
  version: { id: string; content?: Record<string, unknown> | null }
  templateDef: TemplateDefinition
  renderLocale: 'es' | 'en'
  projectId: string
  currentUserId: string | null
  /** OT-0009 — espejo de `_is_transition_allowed` para "Reponer"
   *  comentario: exige líder del proyecto, sin excepción de admin. */
  isLead: boolean
}

function CommentsPanelSlot({
  open,
  onClose,
  artifact,
  version,
  templateDef,
  renderLocale,
  projectId,
  currentUserId,
  isLead,
}: CommentsPanelSlotProps) {
  const { t } = useTranslation('projects')
  // `useRightPanel` devuelve un portal node (o null). Retornamos su
  // resultado como render output: React monta el portal en el DOM del
  // host del shell, sin pasar por estados intermedios.
  return useRightPanel(
    open ? (
      <RightSidePanel
        onClose={onClose}
        ariaLabel={t('comments_drawer.title')}
        tabs={[
          {
            key: 'comments',
            label: t('comments_drawer.title'),
            icon: <MessageSquare className="w-3.5 h-3.5" />,
            content: (
              <CommentsViewerPanel
                artifactId={artifact.id}
                versionId={version.id}
                projectId={projectId}
                currentUserId={currentUserId}
                artifactAuthorId={artifact.created_by_user_id}
                isLead={isLead}
                templateSections={templateDef.sections}
                templateI18nMap={templateI18nSectionMap(templateDef, renderLocale)}
                versionContent={version.content ?? null}
              />
            ),
          },
        ]}
      />
    ) : null,
  )
}

// ─── CommentsTrigger ────────────────────────────────────────────────────────
/** Botón "Comentarios" en el topToolbar del viewer con badge de count.
 *  Mismo styling que el botón del editor (Doc 21 Fase 5.d) para
 *  consistencia visual: estado abierto resalta con bg-brand-soft +
 *  border-brand. Toggleable. */
function CommentsTrigger({
  artifactId,
  versionId,
  isOpen,
  onToggle,
}: {
  artifactId: string
  versionId: string
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('projects')
  const { accessToken } = useAccessToken()
  const countQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', versionId, 'comments'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listVersionComments(accessToken, artifactId, versionId)
    },
    enabled: !!accessToken,
    staleTime: 15_000,
    // Filtramos por kind='comment': el endpoint devuelve también
    // replies y eventos `status_change` (audit trail). Sin el filtro,
    // un comentario con 2 transiciones (pending→addressed→resolved)
    // sumaba 3 al badge aunque visualmente solo hay 1 thread.
    select: (rows) => rows.filter((r) => r.kind === 'comment').length,
  })
  const count = countQuery.data ?? 0
  return (
    <button
      type="button"
      onClick={onToggle}
      className={
        isOpen
          ? 'inline-flex items-center gap-1.5 rounded-md border border-brand bg-brand-soft px-3 py-1.5 text-xs font-medium text-brand transition-colors'
          : 'inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink hover:border-line-strong hover:bg-surface-muted transition-colors'
      }
    >
      <MessageSquare className="w-3.5 h-3.5" />
      {t('comments_drawer.trigger')}
      {count > 0 ? (
        <span
          className={
            isOpen
              ? 'px-1.5 py-0.5 rounded-full bg-brand text-canvas text-[10px] font-mono'
              : 'px-1.5 py-0.5 rounded-full bg-brand-soft text-brand text-[10px] font-mono'
          }
        >
          {count}
        </span>
      ) : null}
    </button>
  )
}

// ─── ErrorBox ───────────────────────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
